import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import getCarCostOfOwnership from "../../functions/vehicle/CostOfOwnership/getCarCostOfOwnership";
import UserPrefsContext from "../../context/UserPrefs/UserPrefsContext";
import CostToOwnComparisonChart from "./CostToOwnComparisonChart/CostToOwnComparisonChart";
import { FormatAsDollars, FormatCarName } from "./../../utils/Helpers/Format";
import { FormattedMessage, useIntl } from "react-intl";
import ToolTip from "../shared/ToolTip/ToolTip";

const CostToOwnComparison = ({
  cars,
  displayEvCostBreakdown,
  displayEvCostBreakdownOption,
  forceUserPrefsPresets,
  hasExtraPadding,
  comparison,
  customLegend,
  insuranceData,
  maintenanceData
}) => {
  const intl = useIntl();
  const userPrefs = useContext(UserPrefsContext);
  const [showCostBreakdown, setShowCostBreakdown] = useState(
    displayEvCostBreakdown
  );

  if (!cars || cars.length === 0) return null;

  cars = cars.filter((car) => {
    return car !== null;
  });

  const get = forceUserPrefsPresets ? userPrefs.getPreset : userPrefs.get;
  const yearsOfOwnership = get("monthsOfOwnership") / 12;

  const carsCosts = cars.map((car) => {
    return getCarCostOfOwnership(car, userPrefs, insuranceData, maintenanceData);
  });

  const sortedCarCosts = [...carsCosts].sort((carCost1, carCost2) => {
    return carCost1.summed.total - carCost2.summed.total;
  });

  let title;

  if (cars.length === 2) {
    const costDelta = carsCosts[1].summed.total - carsCosts[0].summed.total;

    const costDeltaText =
      costDelta < 0
        ? intl.formatMessage({
            id: "graph.moreExpensive",
            defaultMessage: "more expensive",
          })
        : intl.formatMessage({
            id: "graph.cheaper",
            defaultMessage: "cheaper",
          });

    const costDeltaTextEnding =
      intl.formatMessage({
        id: "graph.toOwnOver",
        defaultMessage: "to own over ",
      }) +
      yearsOfOwnership +
      intl.formatMessage({ id: "graph.years", defaultMessage: " years" });

    title = (
      <>
        <p
          className="h3 graph-sub-title"
          style={{
            fontFamily: "TT Norms Pro, Roboto",
            fontWeight: "400",
            fontSize: "16px",
          }}
        >
          <FormattedMessage
            id="graph.costOfOwnership.subTitle"
            defaultMessage="The {carName} is {style} {costDeltaText} {costDeltaTextEnding}"
            description="Graph Sub Title"
            values={{
              carName: FormatCarName(cars[0]),
              style: (
                <span style={{ color: "#EB1700" }}>
                  {FormatAsDollars(Math.abs(costDelta))}
                </span>
              ),
              costDeltaText: costDeltaText,
              costDeltaTextEnding: costDeltaTextEnding,
            }}
          />
        </p>
      </>
    );
  } else {
    title = (
      <>
        <p
          className="h3 graph-sub-title"
          style={{
            fontFamily: "TT Norms Pro, Roboto",
            fontWeight: "400",
            fontSize: "16px",
          }}
        >
          <FormattedMessage
            id="graph.costOfOwnership.subTitleThreeCars"
            defaultMessage="The {carName} is the cheapest to own for {yearsOfOwnership} years"
            description="Graph Sub Title"
            values={{
              carName: FormatCarName(sortedCarCosts[0].car),
              yearsOfOwnership: yearsOfOwnership,
            }}
          />
        </p>
      </>
    );
  }

  const carNames = comparison
    ? cars.map((car) => FormatCarName(car))
    : [
        [FormatCarName(cars[0]), "(Your selection)"],
        [FormatCarName(cars[1]), "(Similar gas vehicle)"],
      ];

  const selectShowCostBreakdown = displayEvCostBreakdownOption ? (
    <div>
      <span className="mr-4">
        <FormattedMessage
          id="graph.costDifferent"
          defaultMessage="Why is the cost different?"
          description="Why is the cost different title"
        />
      </span>
      <div
        className="form-group form-check text-center"
        style={{ display: "inline-block" }}
      >
        <input
          type="checkbox"
          className="form-check-input"
          id="exampleCheck1"
          selected={showCostBreakdown}
          onClick={() => setShowCostBreakdown(!showCostBreakdown)}
        />
        <label className="form-check-label" htmlFor="exampleCheck1">
          <FormattedMessage
            id="graph.showCostBreakdown"
            defaultMessage="Show Cost Breakdown"
            description="Show Cost Breakdown Title"
          />
        </label>
      </div>
    </div>
  ) : null;

  let chartData = {
    labels: carNames,
    datasets: [
      {
        label: intl.formatMessage({
          id: "graph.costOfOwnership.chart.vehicle",
          defaultMessage: "Vehicle net incentives, resale",
        }),
        backgroundColor: `#4C0C3A`,
        data: carsCosts.map((carCost) => {
          return carCost.vehicle.total;
        }),
        tooltip:
          "Electric vehicles are currently often more expensive than traditional internal combustion engines. However, some of this upfront cost can be offset by vehicle incentives, lower costs of ownership, and stronger resale value.",
      },
      {
        label: intl.formatMessage({
          id: "graph.costOfOwnership.chart.electricity",
          defaultMessage: "Electricity",
        }),
        backgroundColor: `#FF3008`,
        data: carsCosts.map((carCost) => {
          return carCost.electricity.total;
        }),
        tooltip:
          "Fueling with electricity is significantly cheaper than fueling with gasoline, for the same distance traveled.",
      },
      {
        label: intl.formatMessage({
          id: "graph.costOfOwnership.chart.gasoline",
          defaultMessage: "Gasoline",
        }),
        backgroundColor: `#FF3008`,
        data: carsCosts.map((carCost) => {
          return carCost.gasoline.total;
        }),
        tooltip:
          "Gasoline is a more expensive fuel for your vehicle. Over time, this expense can become even more pronounced.",
      },
      {
        label: intl.formatMessage({
          id: "graph.costOfOwnership.chart.maintenance",
          defaultMessage: "Maintenance",
        }),
        backgroundColor: `#FFC4FC`,
        data: carsCosts.map((carCost) => {
          return carCost.maintenance.total;
        }),
        tooltip:
          "Maintenance costs are generally lower for electric vehicles than gasoline vehicles due to fewer moving parts.",
      },
      {
        label: intl.formatMessage({
          id: "graph.costOfOwnership.chart.insurance",
          defaultMessage: "Insurance",
        }),
        backgroundColor: `#80D8FF`,
        data: carsCosts.map((carCost) => {
          return carCost.insurance.total;
        }),
        tooltip:
          "EVs can cost more to insure, in large part because they cost more in general. These higher insurance costs are offset by fuel and maintenance savings.",
      },
    ],
  };

  return (
    <div
      className="input-well text-center chartContainer"
      style={hasExtraPadding ? { padding: "2.5rem" } : { paddfing: "1rem" }}
    >
      {title}
      <div className="d-none d-lg-block d-xl-block">
        {selectShowCostBreakdown}
        <div>
          <CostToOwnComparisonChart
            customLegend
            chartData={chartData}
            displayEvCostBreakdownOption={displayEvCostBreakdownOption}
            displayEvCostBreakdown={showCostBreakdown}
          />
        </div>
        {customLegend ? (
          <div
            className="customLegendRow"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {chartData.datasets.map((data, i) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    marginRight: "16px",
                  }}
                >
                  <div
                    style={{
                      height: "12px",
                      width: "12px",
                      backgroundColor: data.backgroundColor,
                      marginRight: "4px",
                    }}
                  />
                  <p
                    style={{
                      fontFamily: "",
                      fontSize: "",
                      fontWeight: "",
                      color: "",
                      marginRight: "4px",
                      marginBottom: "0px",
                    }}
                  >
                    {data.label}
                  </p>
                  {data.tooltip && (
                    <ToolTip
                      message={data.tooltip}
                      id={"legend_tooltip_" + i}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CostToOwnComparison;

CostToOwnComparison.propTypes = {
  car: PropTypes.object,
  displayFuelOnly: PropTypes.bool,
  displayEvCostBreakdown: PropTypes.bool,
  displayEvCostBreakdownOption: PropTypes.bool,
  forceUserPrefsPresets: PropTypes.bool,
  hasExtraPadding: PropTypes.bool,
};
